import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './Products.css';
import './ProductPdpViews.css';
import useProducts from '../hooks/useProducts';
import AdminProductCard from './AdminProductCard';
import AdminLoading from './AdminLoading';
import { analyticsAdminService } from '../../services/api';
import { extractCreatorId } from '../utils/authUtils';
import { hasAccess } from '../constants/teamAccessPermissions';
import {
  buildViewsMap,
  mergeProductViews,
  sumPdpViews,
} from '../utils/pdpViews';

const ProductPdpViews = ({ currentUser, onNavigate, onEditProduct }) => {
  const isTeamMember = currentUser?.role === 'team';
  const myId = String(currentUser?._id || currentUser?.id || '');
  const { products, loading: productsLoading, refetch: refetchProducts } = useProducts();

  const [viewsLoading, setViewsLoading] = useState(true);
  const [totalViews, setTotalViews] = useState(null);
  const [topProducts, setTopProducts] = useState([]);

  const loadViews = useCallback(async () => {
    setViewsLoading(true);
    try {
      const res = await analyticsAdminService.getProductViewsSummary();
      if (res.success) {
        setTotalViews(res.totalViews);
        setTopProducts(Array.isArray(res.topProducts) ? res.topProducts : []);
      } else {
        setTotalViews(null);
        setTopProducts([]);
      }
    } catch {
      setTotalViews(null);
      setTopProducts([]);
    } finally {
      setViewsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadViews();
    const id = window.setInterval(loadViews, 60000);
    return () => window.clearInterval(id);
  }, [loadViews]);

  const scopedProducts = useMemo(() => {
    if (!isTeamMember || !myId) return products || [];
    return (products || []).filter((p) => extractCreatorId(p.createdBy) === myId);
  }, [products, isTeamMember, myId]);

  const viewsMap = useMemo(() => buildViewsMap(topProducts), [topProducts]);

  const productsWithViews = useMemo(() => {
    const merged = mergeProductViews(scopedProducts, viewsMap);
    return [...merged].sort((a, b) => (b.pdpViewCount ?? 0) - (a.pdpViewCount ?? 0));
  }, [scopedProducts, viewsMap]);

  const displayTotalViews = useMemo(() => {
    if (isTeamMember) return sumPdpViews(productsWithViews);
    if (totalViews != null) return totalViews;
    return sumPdpViews(productsWithViews);
  }, [isTeamMember, totalViews, productsWithViews]);

  const loading = productsLoading || viewsLoading;

  const handleRefresh = () => {
    refetchProducts();
    loadViews();
  };

  const handleEdit = (product) => {
    if (onEditProduct) {
      onEditProduct(product);
      return;
    }
    if (onNavigate) onNavigate('add-product');
  };

  if (loading) {
    return (
      <AdminLoading
        title="Loading PDP views"
        subtitle="Fetching products and view counts..."
        icon="👁️"
      />
    );
  }

  return (
    <div className="product-pdp-views-page">
      <div className="product-pdp-views-header">
        <div className="product-pdp-views-header-text">
          <button
            type="button"
            className="product-pdp-views-back"
            onClick={() => onNavigate && onNavigate('dashboard')}
          >
            <i className="fas fa-arrow-left" aria-hidden />
            Dashboard
          </button>
          <h1>
            <i className="fas fa-eye" aria-hidden /> PDP views
          </h1>
          <p>
            {isTeamMember
              ? 'Product detail page views for your listed products.'
              : 'Storefront product detail page views across the catalog.'}
          </p>
        </div>
        <div className="product-pdp-views-summary">
          <span className="product-pdp-views-summary-label">Total PDP views</span>
          <strong className="product-pdp-views-summary-value">
            {displayTotalViews != null ? displayTotalViews.toLocaleString() : '—'}
          </strong>
          <button type="button" className="product-pdp-views-refresh" onClick={handleRefresh}>
            <i className="fas fa-sync-alt" aria-hidden /> Refresh
          </button>
        </div>
      </div>

      {productsWithViews.length === 0 ? (
        <div className="products-empty-state">
          <div className="empty-icon">👁️</div>
          <h3>{isTeamMember ? 'No products to show yet' : 'No products found'}</h3>
          <p>
            {isTeamMember
              ? 'Add products to track PDP views on your creator catalog.'
              : 'Add products to the catalog to track PDP views.'}
          </p>
          {isTeamMember && hasAccess('Products|Create', currentUser) && onNavigate && (
            <button type="button" className="btn-primary" onClick={() => onNavigate('add-product')}>
              <i className="fas fa-plus" aria-hidden /> Add product
            </button>
          )}
        </div>
      ) : (
        <div className="products-grid-wrapper">
          <div className="products-grid">
            {productsWithViews.map((product) => (
              <AdminProductCard
                key={product.id || product._id}
                product={product}
                pdpViewCount={product.pdpViewCount}
                onEdit={handleEdit}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductPdpViews;
